import React from 'react';
import { useExportCodingsMutation } from './mutations';
import { useProject } from '../projects/queries';
import { useAllProjectTranscripts } from '../transcripts/queries';
import { useDescriptors } from '../descriptors/queries';
import { useCodes } from '../codes/queries';
import { useProjectUsers } from '../users/queries';

/**
 * Hook for the long-format codings export (used on the reports page).
 * Same data as the snippets export, but the mapper emits one row per coding.
 */
export function useCodingsExport(projectId, filters = {}) {
  const { data: projectData, isLoading: projectLoading } = useProject(projectId);
  // Gate on isFetching (not just isLoading): useAllProjectTranscripts returns
  // the first 500 transcripts immediately and keeps fetching the rest in the
  // background — exporting mid-fetch would mislabel rows as Untitled.
  const { data: transcripts, isFetching: transcriptsFetching } = useAllProjectTranscripts(projectId);
  const { data: descriptorsData, isLoading: descriptorsLoading } = useDescriptors(projectId);
  const { data: codesData, isLoading: codesLoading } = useCodes(projectId);
  const { data: usersData, isLoading: usersLoading } = useProjectUsers(projectId);
  const mutation = useExportCodingsMutation(projectData, descriptorsData, codesData?.codes, usersData, transcripts, filters);

  return {
    exportCodings: () => mutation.mutate(),
    isExporting: mutation.isPending,
    error: mutation.error,
    // Data presence checks keep the button disabled when a query errored out
    // (isLoading settles false with data undefined), which would otherwise
    // export a plausible-looking but incomplete CSV
    isReady: !!projectId && !projectLoading && !!projectData &&
      !transcriptsFetching && !!transcripts &&
      !descriptorsLoading && !!descriptorsData &&
      !codesLoading && !!codesData &&
      !usersLoading && !!usersData
  };
}
